/**
 * Client Progress Report PDF Generator
 *
 * Generates a professional multi-section PDF progress report for a client,
 * including sessions, measurements, workouts, and nutrition data.
 *
 * NOTE: jsPDF and jspdf-autotable are dynamically imported to reduce bundle size.
 */

import { createPDFDownload } from './pdf-generator';
import { format } from 'date-fns';

// Reuse the JsPDF type from existing generators
type JsPDFInstance = {
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  setFontSize: (size: number) => void;
  setFont: (fontName: string, fontStyle?: string) => void;
  setTextColor: (...color: number[]) => void;
  setDrawColor: (...color: number[]) => void;
  setFillColor: (...color: number[]) => void;
  setLineWidth: (width: number) => void;
  text: (text: string, x: number, y: number, options?: { align?: string }) => void;
  line: (x1: number, y1: number, x2: number, y2: number) => void;
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  addPage: () => void;
  output: (type: 'blob') => Blob;
  autoTable: (options: AutoTableOptions) => void;
  lastAutoTable: { finalY: number };
};

interface AutoTableOptions {
  head?: string[][];
  body?: string[][];
  startY?: number;
  theme?: 'striped' | 'grid' | 'plain';
  headStyles?: Record<string, unknown>;
  bodyStyles?: Record<string, unknown>;
  alternateRowStyles?: Record<string, unknown>;
  footStyles?: Record<string, unknown>;
  foot?: string[][];
  margin?: { top?: number; left?: number; right?: number; bottom?: number };
  didDrawPage?: (data: { pageNumber: number }) => void;
  columnStyles?: Record<number, { cellWidth?: number | 'auto' | 'wrap'; halign?: string; fontStyle?: string }>;
  showFoot?: 'everyPage' | 'lastPage' | 'never';
}

const COLORS = {
  primary: [59, 130, 246] as [number, number, number],
  headerBg: [243, 244, 246] as [number, number, number],
  textDark: [31, 41, 55] as [number, number, number],
  textMuted: [107, 114, 128] as [number, number, number],
  green: [22, 163, 74] as [number, number, number],
  red: [220, 38, 38] as [number, number, number],
  summaryBg: [249, 250, 251] as [number, number, number],
  accent: [124, 58, 237] as [number, number, number], // Purple for highlights
};

async function loadJsPDF(): Promise<{
  jsPDF: new (options: { orientation: string; unit: string; format: string }) => JsPDFInstance;
}> {
  const [{ jsPDF }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  return {
    jsPDF: jsPDF as unknown as new (options: { orientation: string; unit: string; format: string }) => JsPDFInstance,
  };
}

/** Report data shape returned by the generate-progress-report edge function */
export interface ProgressReportData {
  client: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    status: string;
    member_since: string;
  };
  trainer: {
    name: string;
  };
  date_range: {
    from: string;
    to: string;
  };
  sessions: {
    total_scheduled: number;
    completed: number;
    cancelled: number;
    attendance_rate: number;
  };
  package: {
    size: number;
    sessions_completed: number;
    sessions_remaining: number;
    progress_pct: number;
  } | null;
  measurements: {
    count: number;
    deltas: Record<
      string,
      { start: number | null; current: number | null; delta: number | null }
    >;
    history: Array<Record<string, unknown>>;
  };
  workouts: {
    total_workouts: number;
    total_volume: number;
    total_sets: number;
    avg_duration_minutes: number;
    history: Array<{
      date: string;
      name: string;
      volume: number;
      sets: number;
      exercises: number;
      duration: number;
    }>;
  };
  nutrition: {
    days_logged: number;
    avg_calories: number;
    avg_protein: number;
    avg_carbs: number;
    avg_fat: number;
    calorie_adherence_pct: number | null;
  };
  generated_at: string;
}

const METRIC_LABELS: Record<string, { label: string; unit: string }> = {
  weight: { label: 'Weight', unit: 'lbs' },
  body_fat_percentage: { label: 'Body Fat', unit: '%' },
  waist: { label: 'Waist', unit: 'in' },
  chest: { label: 'Chest', unit: 'in' },
  hips: { label: 'Hips', unit: 'in' },
  arms: { label: 'Arms', unit: 'in' },
  thighs: { label: 'Thighs', unit: 'in' },
};

function fmtNum(value: number | null, decimals = 1): string {
  if (value == null) return '-';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

function fmtDelta(value: number | null, unit: string): string {
  if (value == null) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${fmtNum(value)} ${unit}`;
}

function addFooter(
  doc: JsPDFInstance,
  pageWidth: number,
  pageHeight: number,
  margin: number,
  data: { pageNumber: number },
) {
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.textMuted);
  doc.text(`Page ${data.pageNumber}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  doc.text('TrainerPulse', margin, pageHeight - 10);
}

function checkPageBreak(
  doc: JsPDFInstance,
  currentY: number,
  neededSpace: number,
  pageHeight: number,
): number {
  if (currentY + neededSpace > pageHeight - 20) {
    doc.addPage();
    return 20;
  }
  return currentY;
}

/**
 * Generate a client progress report PDF.
 *
 * @param report - Structured data from the edge function
 * @param trainerNotes - Optional custom notes from the trainer
 */
export async function generateProgressReportPDF(
  report: ProgressReportData,
  trainerNotes?: string,
): Promise<Blob> {
  const { jsPDF } = await loadJsPDF();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  // ── Header ────────────────────────────────────────────────────────────
  doc.setFontSize(22);
  doc.setTextColor(...COLORS.primary);
  doc.text('Client Progress Report', margin, 22);

  doc.setFontSize(14);
  doc.setTextColor(...COLORS.textDark);
  doc.text(report.client.name, margin, 32);

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.textMuted);
  const rangeLabel = `${format(new Date(report.date_range.from), 'MMM d, yyyy')} — ${format(new Date(report.date_range.to), 'MMM d, yyyy')}`;
  doc.text(rangeLabel, margin, 39);
  doc.text(`Trainer: ${report.trainer.name}`, margin, 45);
  doc.text(`Generated: ${format(new Date(), 'MMMM d, yyyy h:mm a')}`, margin, 51);

  // ── Summary Cards ─────────────────────────────────────────────────────
  const boxY = 58;
  const boxH = 28;
  doc.setFillColor(...COLORS.summaryBg);
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.3);
  doc.rect(margin, boxY, contentWidth, boxH, 'FD');

  const cols = report.package ? 4 : 3;
  const colWidth = contentWidth / cols;

  // Sessions completed
  let colIdx = 0;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textMuted);
  doc.text('Sessions Completed', margin + colWidth * (colIdx + 0.5), boxY + 8, { align: 'center' });
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.primary);
  doc.text(`${report.sessions.completed}/${report.sessions.total_scheduled}`, margin + colWidth * (colIdx + 0.5), boxY + 18, { align: 'center' });

  // Attendance rate
  colIdx++;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textMuted);
  doc.text('Attendance Rate', margin + colWidth * (colIdx + 0.5), boxY + 8, { align: 'center' });
  doc.setFontSize(16);
  doc.setTextColor(...(report.sessions.attendance_rate >= 80 ? COLORS.green : report.sessions.attendance_rate >= 60 ? COLORS.primary : COLORS.red));
  doc.text(`${report.sessions.attendance_rate}%`, margin + colWidth * (colIdx + 0.5), boxY + 18, { align: 'center' });

  // Workouts
  colIdx++;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textMuted);
  doc.text('Workouts Logged', margin + colWidth * (colIdx + 0.5), boxY + 8, { align: 'center' });
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.primary);
  doc.text(String(report.workouts.total_workouts), margin + colWidth * (colIdx + 0.5), boxY + 18, { align: 'center' });

  // Package progress (if applicable)
  if (report.package) {
    colIdx++;
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textMuted);
    doc.text('Package Progress', margin + colWidth * (colIdx + 0.5), boxY + 8, { align: 'center' });
    doc.setFontSize(16);
    doc.setTextColor(...COLORS.accent);
    doc.text(`${report.package.progress_pct}%`, margin + colWidth * (colIdx + 0.5), boxY + 18, { align: 'center' });
  }

  let currentY = boxY + boxH + 12;

  // ── Measurements Table ────────────────────────────────────────────────
  const deltaEntries = Object.entries(report.measurements.deltas);
  if (deltaEntries.length > 0) {
    currentY = checkPageBreak(doc, currentY, 40, pageHeight);
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.textDark);
    doc.text('Body Measurements', margin, currentY);
    currentY += 3;

    const measBody = deltaEntries.map(([key, val]) => {
      const meta = METRIC_LABELS[key] || { label: key, unit: '' };
      return [
        meta.label,
        val.start != null ? `${fmtNum(val.start)} ${meta.unit}` : '-',
        val.current != null ? `${fmtNum(val.current)} ${meta.unit}` : '-',
        fmtDelta(val.delta, meta.unit),
      ];
    });

    doc.autoTable({
      head: [['Metric', 'Start', 'Current', 'Change']],
      body: measBody,
      startY: currentY,
      theme: 'striped',
      headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.textDark, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLORS.textDark },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: { 3: { halign: 'right' } },
      margin: { left: margin, right: margin },
      didDrawPage: (data) => addFooter(doc, pageWidth, pageHeight, margin, data),
    });
    currentY = doc.lastAutoTable.finalY + 10;
  }

  // ── Workout Highlights ────────────────────────────────────────────────
  if (report.workouts.total_workouts > 0) {
    currentY = checkPageBreak(doc, currentY, 50, pageHeight);
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.textDark);
    doc.text('Workout Highlights', margin, currentY);
    currentY += 8;

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textDark);
    const workoutStats = [
      `Total Workouts: ${report.workouts.total_workouts}`,
      `Total Volume: ${report.workouts.total_volume.toLocaleString()} lbs`,
      `Total Sets: ${report.workouts.total_sets.toLocaleString()}`,
      `Avg Duration: ${report.workouts.avg_duration_minutes} min`,
    ];
    for (const stat of workoutStats) {
      doc.text(`  ${stat}`, margin, currentY);
      currentY += 6;
    }
    currentY += 2;

    // Recent workouts table (show up to 10)
    if (report.workouts.history.length > 0) {
      const workoutBody = report.workouts.history.slice(-10).map((w) => [
        format(new Date(w.date), 'MMM d'),
        w.name || 'Untitled',
        `${(w.volume || 0).toLocaleString()} lbs`,
        String(w.sets || 0),
        w.duration ? `${w.duration} min` : '-',
      ]);

      doc.autoTable({
        head: [['Date', 'Workout', 'Volume', 'Sets', 'Duration']],
        body: workoutBody,
        startY: currentY,
        theme: 'striped',
        headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.textDark, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 8, textColor: COLORS.textDark },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: margin, right: margin },
        didDrawPage: (data) => addFooter(doc, pageWidth, pageHeight, margin, data),
      });
      currentY = doc.lastAutoTable.finalY + 10;
    }
  }

  // ── Nutrition Summary ─────────────────────────────────────────────────
  if (report.nutrition.days_logged > 0) {
    currentY = checkPageBreak(doc, currentY, 50, pageHeight);
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.textDark);
    doc.text('Nutrition Summary', margin, currentY);
    currentY += 3;

    const nutritionBody = [
      ['Days Logged', String(report.nutrition.days_logged)],
      ['Avg Daily Calories', `${report.nutrition.avg_calories.toLocaleString()} kcal`],
      ['Avg Protein', `${report.nutrition.avg_protein}g`],
      ['Avg Carbs', `${report.nutrition.avg_carbs}g`],
      ['Avg Fat', `${report.nutrition.avg_fat}g`],
    ];
    if (report.nutrition.calorie_adherence_pct != null) {
      nutritionBody.push(['Calorie Goal Adherence', `${report.nutrition.calorie_adherence_pct}%`]);
    }

    doc.autoTable({
      head: [['Metric', 'Value']],
      body: nutritionBody,
      startY: currentY,
      theme: 'striped',
      headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.textDark, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLORS.textDark },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: margin, right: margin },
      didDrawPage: (data) => addFooter(doc, pageWidth, pageHeight, margin, data),
    });
    currentY = doc.lastAutoTable.finalY + 10;
  }

  // ── Trainer Notes ─────────────────────────────────────────────────────
  if (trainerNotes && trainerNotes.trim()) {
    currentY = checkPageBreak(doc, currentY, 30, pageHeight);
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.textDark);
    doc.text('Trainer Notes', margin, currentY);
    currentY += 8;

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textDark);

    // Word-wrap the notes
    const maxLineWidth = contentWidth - 10;
    const words = trainerNotes.trim().split(/\s+/);
    let line = '';
    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      // Approximate: 10pt font ~ 0.35mm per char average
      if (testLine.length * 1.8 > maxLineWidth && line) {
        doc.text(`  ${line}`, margin, currentY);
        currentY += 5;
        currentY = checkPageBreak(doc, currentY, 10, pageHeight);
        line = word;
      } else {
        line = testLine;
      }
    }
    if (line) {
      doc.text(`  ${line}`, margin, currentY);
      currentY += 5;
    }
  }

  return doc.output('blob');
}

export { createPDFDownload };
